<template>
  <div v-if="loading" class="inline-flex flex-col" aria-busy="true" :aria-label="label ? `${label} loading` : 'Loading'" data-test="filter-dropdown-loading">
    <!-- Loading: placeholders for the label and the box, like Worklist_FilterBar's skeleton, at the same width. -->
    <div class="h-3 w-12 mb-1.5 rounded bg-gray-200 animate-pulse" />
    <div class="h-[34px] rounded-lg border border-gray-200 bg-gray-100 animate-pulse" :class="fullWidth ? 'w-full' : 'w-36'" />
  </div>
  <div v-else class="inline-flex flex-col gap-0.5">
    <!-- Label -->
    <label v-if="label" :id="labelId" class="text-xs font-semibold">
      {{ label }}
    </label>

    <!-- Dropdown -->
    <div class="relative" :class="fullWidth ? 'block w-full' : 'inline-block'" data-test="filter-dropdown-box">

      <!-- Hidden sizer to auto-width based on longest option (not with fullWidth: the page sets the width) -->
      <div v-if="!fullWidth" class="invisible h-0 overflow-hidden flex items-center gap-2 px-3 text-sm font-medium" aria-hidden="true">
        <font-awesome-icon v-if="icon" :icon="icon" class="text-sm" />
        <span :class="{ sensitive: sensitiveLabels }">{{ longestLabel }}</span>
        <font-awesome-icon :icon="['fas', 'chevron-down']" class="text-xs" />
      </div>

      <button
        ref="trigger"
        type="button"
        role="combobox"
        :aria-expanded="isOpen"
        :aria-haspopup="'listbox'"
        :aria-labelledby="label ? labelId : undefined"
        :aria-label="label ? undefined : ariaLabel"
        :aria-activedescendant="isOpen && focusedIndex >= 0 ? optionId(focusedIndex) : undefined"
        class="w-full flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm font-medium transition-colors"
        :class="isDisabled ? 'text-gray-400 opacity-50' : 'text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'"
        :disabled="isDisabled"
        @click="toggleDropdown"
        @keydown="handleTriggerKeydown"
      >
        <!-- Icon -->
        <font-awesome-icon
          v-if="icon"
          :icon="icon"
          class="text-gray-600 text-sm"
        />

        <!-- Selected Value -->
        <span class="min-w-0 truncate text-left" :class="{ sensitive: sensitiveLabels && modelValue !== null }">{{ displayValue }}</span>

        <!-- Chevron -->
        <font-awesome-icon
          :icon="['fas', 'chevron-down']"
          class="text-gray-500 text-xs ml-auto"
          :class="{ 'rotate-180': isOpen }"
        />
      </button>

      <!-- Dropdown Menu -->
      <Teleport to="body">
      <div
        v-if="isOpen"
        ref="listbox"
        role="listbox"
        :aria-labelledby="label ? labelId : undefined"
        class="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-50"
        :style="dropdownStyle"
      >
        <div v-if="searchable" class="border-b border-gray-200 p-2">
          <input
            ref="search"
            v-model="query"
            type="text"
            class="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            :placeholder="searchPlaceholder"
            :aria-label="searchPlaceholder"
            data-test="filter-dropdown-search"
            @keydown="handleTriggerKeydown"
          >
        </div>
        <div
          class="py-1 overflow-y-auto"
          :class="{ 'max-h-60': !listMaxHeight, 'filter-dropdown-scrollbar': visibleScrollbar }"
          :style="listStyle"
          data-test="filter-dropdown-list"
        >
          <div
            v-if="clearable"
            :id="optionId(-1)"
            role="option"
            :aria-selected="modelValue === null"
            class="w-full text-left px-4 py-2 text-sm cursor-pointer transition-colors"
            :class="[
              modelValue === null ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-400',
              focusedIndex === -1 && isOpen ? 'bg-gray-100' : ''
            ]"
            @click="selectOption({ value: null, label: placeholder })"
            @mouseenter="focusedIndex = -1"
          >
            {{ placeholder }}
          </div>
          <template v-for="(option, index) in visibleOptions" :key="option.value">
          <!-- Optional one-level grouping: a header wherever an option's group differs from the one before. -->
          <div
            v-if="option.group && option.group !== visibleOptions[index - 1]?.group"
            role="presentation"
            class="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400"
            data-test="filter-dropdown-group"
          >
            {{ option.group }}
          </div>
          <div
            :id="optionId(index)"
            role="option"
            :aria-selected="option.value === modelValue"
            class="w-full text-left pr-4 py-2 text-sm cursor-pointer transition-colors"
            :class="[
              // Indent options under their group header, like a native optgroup
              option.group ? 'pl-8' : 'pl-4',
              option.value === modelValue
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'text-gray-700',
              focusedIndex === index ? 'bg-gray-100' : ''
            ]"
            @click="selectOption(option)"
            @mouseenter="focusedIndex = index"
          >
            <span :class="{ sensitive: sensitiveLabels }">{{ option.label }}</span>
          </div>
          </template>
        </div>
      </div>
      </Teleport>

    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

let uid = 0;

export default {
  name: 'FilterDropdown',
  components: {
    FontAwesomeIcon
  },
  props: {
    sensitiveLabels: { type: Boolean, default: false },
    // Label text to display before dropdown
    label: {
      type: String,
      default: null
    },
    // FontAwesome icon array, e.g. ['fas', 'calendar']
    icon: {
      type: Array,
      default: null
    },
    // Current selected value
    modelValue: {
      type: [String, Number],
      default: null
    },
    // Array of options - objects with { value, label }, and an optional group shown as a header
    // (options of one group must be next to each other)
    options: {
      type: Array,
      required: true
    },
    // Default text when no value selected
    placeholder: {
      type: String,
      default: 'No filter'
    },
    // Show a clearable "No filter" option at the top of the dropdown
    clearable: {
      type: Boolean,
      default: false
    },
    // Fill the width the page gives it (e.g. class="w-56") instead of sizing to the longest option, so it doesn't
    // change size when options load. Long labels are cut off with an ellipsis.
    fullWidth: {
      type: Boolean,
      default: false
    },
    // Names the box for screen readers when there is no visible label
    ariaLabel: {
      type: String,
      default: null
    },
    // Its options are still loading: show a placeholder instead of an empty, disabled dropdown
    loading: {
      type: Boolean,
      default: false
    },
    // Show a search box that narrows the options by label (for long lists)
    searchable: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: 'Search…'
    },
    // CSS max-height for the option list (e.g. '70vh') when it should be taller than the default 15rem.
    // Never taller than the space left below the trigger, so the menu stays on screen and scrolls instead.
    listMaxHeight: {
      type: String,
      default: null
    },
    // Keep the list's scrollbar on screen (macOS hides it until you scroll), so people can tell there is more below
    visibleScrollbar: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      isOpen: false,
      focusedIndex: -1,
      dropdownPos: { top: 0, left: 0, minWidth: 0, spaceBelow: 0 },
      query: ''

    };
  },
  watch: {
    // Typing changes the list, so start again from the top of it.
    query() {
      this.focusedIndex = this.clearable ? -1 : 0;
    }
  },
  created() {
    this._uid = uid++;
  },
  computed: {
    labelId() {
      return `filter-dropdown-label-${this._uid}`;
    },
    visibleOptions() {
      const terms = this.query.trim().toLowerCase().split(/\s+/).filter(Boolean);
      if (!this.searchable || !terms.length) return this.options;
      // Every word typed must appear in the label, in any order.
      return this.options.filter(opt => {
        const label = String(opt.label || '').toLowerCase();
        return terms.every(t => label.includes(t));
      });
    },
    isDisabled() {
      return !this.options || this.options.length === 0;
    },
    displayValue() {
      if (this.modelValue === null || this.modelValue === undefined) {
        return this.placeholder;
      }
      const option = this.options.find(opt => opt.value === this.modelValue);
      return option ? option.label : this.placeholder;
    },
    longestLabel() {
      if (!this.options || this.options.length === 0) return this.placeholder;
      return this.options.reduce((a, b) =>
        (a.label || '').length >= (b.label || '').length ? a : b
      ).label || this.placeholder;
    },
    dropdownStyle() {
      return {
        top: `${this.dropdownPos.top}px`,
        left: `${this.dropdownPos.left}px`,
        minWidth: `${this.dropdownPos.minWidth}px`
      };
    },
    listStyle() {
      if (!this.listMaxHeight) return null;
      return { maxHeight: `min(${this.listMaxHeight}, ${this.dropdownPos.spaceBelow}px)` };
    }
  },
  methods: {
    optionId(index) {
      return `filter-dropdown-option-${this._uid}-${index}`;
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.query = '';
        const selectedIndex = this.options.findIndex(opt => opt.value === this.modelValue);
        this.focusedIndex = selectedIndex >= 0 ? selectedIndex : (this.clearable ? -1 : 0);
        this.updateDropdownPosition();
        if (this.searchable) this.$nextTick(() => this.$refs.search?.focus());
      }
    },
    updateDropdownPosition() {
      const trigger = this.$refs.trigger;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      this.dropdownPos = {
        top: rect.bottom + 4,
        left: rect.left,
        minWidth: rect.width,
        // Room from the menu's top to the bottom of the window, less the search box and a small margin
        spaceBelow: Math.max(window.innerHeight - rect.bottom - 12 - (this.searchable ? 49 : 0), 160)
      };
    },
    closeDropdown() {
      this.isOpen = false;
      this.focusedIndex = -1;
    },
    selectOption(option) {
      this.$emit('update:modelValue', option.value);
      this.$emit('change', option.value);
      this.closeDropdown();
      this.$refs.trigger?.focus();
    },
    handleTriggerKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!this.isOpen) {
            this.toggleDropdown();
          } else {
            this.focusedIndex = Math.min(this.focusedIndex + 1, this.visibleOptions.length - 1);
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (this.isOpen) {
            this.focusedIndex = Math.max(this.focusedIndex - 1, this.clearable ? -1 : 0);
          }
          break;
        case ' ':
          if (event.target === this.$refs.search) break;
        // falls through
        case 'Enter':
          event.preventDefault();
          if (this.isOpen) {
            if (this.clearable && this.focusedIndex === -1) {
              this.selectOption({ value: null, label: this.placeholder });
            } else if (this.focusedIndex >= 0) {
              const option = this.visibleOptions[this.focusedIndex];
              if (option) this.selectOption(option);
            }
          } else {
            this.toggleDropdown();
          }
          break;
        case 'Escape':
          if (this.isOpen) {
            event.preventDefault();
            this.closeDropdown();
            this.$refs.trigger?.focus();
          }
          break;
        case 'Home':
          if (this.isOpen) {
            event.preventDefault();
            this.focusedIndex = this.clearable ? -1 : 0;
          }
          break;
        case 'End':
          if (this.isOpen) {
            event.preventDefault();
            this.focusedIndex = this.visibleOptions.length - 1;
          }
          break;
      }
    },
    handleClickOutside(event) {
      const element = this.$el;
      const listbox = this.$refs.listbox;
      if (element && !element.contains(event.target) && (!listbox || !listbox.contains(event.target))) {
        this.closeDropdown();
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

button svg {
  transition: transform 0.2s ease;
}

/* Styling the scrollbar makes Chrome and Edge always draw it, instead of macOS's hide-until-scrolled overlay */
.filter-dropdown-scrollbar::-webkit-scrollbar {
  width: 10px;
}

.filter-dropdown-scrollbar::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 0 0.5rem 0.5rem 0;
}

.filter-dropdown-scrollbar::-webkit-scrollbar-thumb {
  background: #9ca3af;
  border: 2px solid #f3f4f6;
  border-radius: 9999px;
}
</style>
